import cn from "classnames";
import { useState, MouseEvent, useEffect } from "react";
import { Text } from "@rshbintech.dboul/core-ui";
import { RatingProps } from "./types";
import { Star } from "./star";
import { NumberBlock } from "./number-block/number-block";
import { Emoji } from "./emoji/emoji";
import styles from "./styles.scss";

export const Rating = ({
  type,
  defaultValue = -1,
  maxValue = 5,
  onChange,
  minValueDescription,
  maxValueDescription,
  descriptions,
  className,
}: RatingProps) => {
  const [rating, setRating] = useState(defaultValue);
  const [hover, setHover] = useState(defaultValue);
  const [active, setActive] = useState(-1);

  const isNeedToShowDescription = minValueDescription || maxValueDescription;

  const handleClick = (event: MouseEvent<HTMLButtonElement>, value: number) => {
    setRating(value);
    onChange?.(event, value);
    setActive(value);
  };

  useEffect(() => {
    setActive(-1);
  }, [hover]);

  return (
    <div className={cn(styles.rating, className)}>
      {[...Array(type.startsWith("number") ? maxValue + 1 : maxValue)].map(
        (_, index) => {
          let ratingComponent: JSX.Element;

          switch (type) {
            case "number-monochrome":

            case "number-colorful":
              ratingComponent = (
                <NumberBlock
                  index={index}
                  rating={rating}
                  hover={hover}
                  active={active}
                  type={type}
                />
              );
              break;

            case "star":
              ratingComponent = (
                <Star
                  index={index}
                  rating={rating}
                  hover={hover}
                  active={active}
                />
              );
              break;

            case "emoji":
              ratingComponent = (
                <Emoji
                  index={index}
                  rating={rating}
                  hover={hover}
                  active={active}
                />
              );
              break;

            default:
              ratingComponent = <></>;
          }

          return (
            <div className={cn(styles.wrapper, styles[type])} key={index}>
              <button
                type="button"
                className={styles.button}
                key={index}
                onClick={(event) =>
                  handleClick(
                    event,
                    type.startsWith("number") ? index : index + 1
                  )
                }
                onMouseEnter={() => {
                  setHover(type.startsWith("number") ? index : index + 1);
                }}
                onMouseLeave={() => {
                  setHover(-1);
                }}
              >
                {ratingComponent}
              </button>

              {isNeedToShowDescription ? (
                <>
                  {index === 0 && (
                    <Text
                      className={cn(
                        styles.description,
                        styles[`description-${type}`],
                        styles[`description-${type}-start`]
                      )}
                      size="xsmall"
                    >
                      {minValueDescription}
                    </Text>
                  )}
                  {(type.startsWith("number")
                    ? index === maxValue
                    : index === maxValue - 1) && (
                    <Text
                      className={cn(
                        styles.description,
                        styles[`description-${type}`],
                        styles[`description-${type}-end`]
                      )}
                      size="xsmall"
                    >
                      {maxValueDescription}
                    </Text>
                  )}
                </>
              ) : (
                (type.startsWith("number")
                  ? index === hover
                  : index === hover - 1) && (
                  <Text
                    className={cn(styles.description, styles["no-height"])}
                    size="xsmall"
                  >
                    {descriptions
                      ? descriptions[
                          type.startsWith("number") ? hover : hover - 1
                        ]
                      : null}
                  </Text>
                )
              )}
            </div>
          );
        }
      )}
    </div>
  );
};
